import { FaceLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

const WASM_ROOT = '/wasm';
const MODEL_PATH = '/models/face_landmarker.task';
const DEFAULT_EYE_Z = 3.2;

export class HeadTracker {
  constructor(video, { onEye, onState }) {
    this.video = video;
    this.onEye = onEye;
    this.onState = onState;
    this.landmarker = null;
    this.stream = null;
    this.active = false;
    this.faceVisible = false;
    this.neutral = null;
    this.latestMeasurement = null;
    this.lastVideoTime = -1;
    this.lastDetectionAt = 0;
    this.frameHandle = 0;
  }

  async start() {
    if (this.active) return;
    if (!navigator.mediaDevices?.getUserMedia) {
      throw new Error('Kamera wird in diesem Browser nicht unterstützt.');
    }

    this.onState('requesting', 'Kamerazugriff');
    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: 'user',
        width: { ideal: 960 },
        height: { ideal: 540 },
        frameRate: { ideal: 30, max: 30 },
      },
      audio: false,
    });

    this.video.srcObject = this.stream;
    await this.video.play();
    this.onState('loading', 'Tracking wird geladen');

    try {
      if (!this.landmarker) this.landmarker = await this.createLandmarker();
      this.active = true;
      this.lastVideoTime = -1;
      this.onState('active', 'Kamera aktiv');
      this.scheduleFrame();
    } catch (error) {
      this.stop();
      throw error;
    }
  }

  async createLandmarker() {
    const vision = await FilesetResolver.forVisionTasks(WASM_ROOT);
    const options = {
      baseOptions: {
        modelAssetPath: MODEL_PATH,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numFaces: 1,
      minFaceDetectionConfidence: 0.55,
      minFacePresenceConfidence: 0.55,
      minTrackingConfidence: 0.5,
      outputFaceBlendshapes: false,
      outputFacialTransformationMatrixes: false,
    };

    try {
      return await FaceLandmarker.createFromOptions(vision, options);
    } catch (gpuError) {
      console.warn('MediaPipe GPU delegate unavailable, using CPU.', gpuError);
      return FaceLandmarker.createFromOptions(vision, {
        ...options,
        baseOptions: { modelAssetPath: MODEL_PATH },
      });
    }
  }

  scheduleFrame() {
    if (!this.active) return;
    if ('requestVideoFrameCallback' in this.video) {
      this.frameHandle = this.video.requestVideoFrameCallback(() => this.processFrame());
    } else {
      this.frameHandle = requestAnimationFrame(() => this.processFrame());
    }
  }

  processFrame() {
    if (!this.active || !this.landmarker) return;

    if (this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && this.video.currentTime !== this.lastVideoTime) {
      this.lastVideoTime = this.video.currentTime;
      try {
        const result = this.landmarker.detectForVideo(this.video, performance.now());
        const landmarks = result.faceLandmarks?.[0];
        if (landmarks?.length) {
          const measurement = measureFace(landmarks);
          this.latestMeasurement = measurement;
          if (!this.neutral) this.neutral = { ...measurement };
          this.lastDetectionAt = performance.now();
          if (!this.faceVisible) {
            this.faceVisible = true;
            this.onState('tracking', 'Gesicht erkannt');
          }
          this.onEye(this.measurementToEye(measurement));
        } else if (performance.now() - this.lastDetectionAt > 650 && this.faceVisible) {
          this.faceVisible = false;
          this.onState('active', 'Gesicht suchen');
        }
      } catch (error) {
        console.error('Face tracking frame failed.', error);
      }
    }

    this.scheduleFrame();
  }

  measurementToEye(measurement) {
    const neutral = this.neutral || measurement;
    const distanceRatio = clamp(neutral.interEye / Math.max(measurement.interEye, 0.015), 0.5, 1.7);
    const z = clamp(DEFAULT_EYE_Z * distanceRatio, 1.55, 5.2);
    const x = clamp((neutral.x - measurement.x) * z * 1.65, -1.65, 1.65);
    const y = clamp((neutral.y - measurement.y) * z * 1.65, -1.0, 1.0);
    return { x, y, z };
  }

  calibrate() {
    if (!this.latestMeasurement) return false;
    this.neutral = { ...this.latestMeasurement };
    this.onEye({ x: 0, y: 0, z: DEFAULT_EYE_Z });
    return true;
  }

  stop() {
    this.active = false;
    this.faceVisible = false;
    if ('cancelVideoFrameCallback' in this.video && this.frameHandle) {
      this.video.cancelVideoFrameCallback(this.frameHandle);
    } else if (this.frameHandle) {
      cancelAnimationFrame(this.frameHandle);
    }
    this.frameHandle = 0;
    this.stream?.getTracks().forEach((track) => track.stop());
    this.stream = null;
    this.video.srcObject = null;
    this.onState('stopped', 'Kamera aus');
  }
}

export class FallbackController {
  constructor(element, onEye) {
    this.element = element;
    this.onEye = onEye;
    this.lastInputAt = performance.now();
    this.pointerActive = false;
    this._pointerMove = (event) => this.handlePointer(event);
    this._pointerDown = (event) => {
      this.pointerActive = true;
      this.handlePointer(event);
    };
    this._pointerUp = () => { this.pointerActive = false; };
    element.addEventListener('pointermove', this._pointerMove, { passive: true });
    element.addEventListener('pointerdown', this._pointerDown, { passive: true });
    element.addEventListener('pointerup', this._pointerUp, { passive: true });
    element.addEventListener('pointercancel', this._pointerUp, { passive: true });
  }

  handlePointer(event) {
    if (event.pointerType === 'touch' && !this.pointerActive) return;
    const rect = this.element.getBoundingClientRect();
    const nx = (event.clientX - rect.left) / Math.max(1, rect.width) - 0.5;
    const ny = 0.5 - (event.clientY - rect.top) / Math.max(1, rect.height);
    this.lastInputAt = performance.now();
    this.onEye({
      x: clamp(nx * 2.35, -1.4, 1.4),
      y: clamp(ny * 1.35, -0.82, 0.82),
      z: 3.2,
    }, event.pointerType === 'touch' ? 'touch' : 'mouse');
  }

  destroy() {
    this.element.removeEventListener('pointermove', this._pointerMove);
    this.element.removeEventListener('pointerdown', this._pointerDown);
    this.element.removeEventListener('pointerup', this._pointerUp);
    this.element.removeEventListener('pointercancel', this._pointerUp);
  }
}

function measureFace(landmarks) {
  const leftEye = averagePoint(landmarks, landmarks[468] ? [468] : [33, 133]);
  const rightEye = averagePoint(landmarks, landmarks[473] ? [473] : [362, 263]);
  const nose = landmarks[1] || { x: (leftEye.x + rightEye.x) / 2, y: (leftEye.y + rightEye.y) / 2 };
  const eyeMid = {
    x: (leftEye.x + rightEye.x) * 0.5,
    y: (leftEye.y + rightEye.y) * 0.5,
  };

  return {
    x: eyeMid.x * 0.82 + nose.x * 0.18,
    y: eyeMid.y * 0.78 + nose.y * 0.22,
    interEye: Math.hypot(leftEye.x - rightEye.x, leftEye.y - rightEye.y),
  };
}

function averagePoint(landmarks, indices) {
  const total = indices.reduce((sum, index) => {
    const point = landmarks[index];
    sum.x += point?.x || 0;
    sum.y += point?.y || 0;
    return sum;
  }, { x: 0, y: 0 });
  return { x: total.x / indices.length, y: total.y / indices.length };
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}
